import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { 
  DEFAULT_SCRAMBLE_WORDS, 
  getHouseScores, 
  saveHouseScores, 
  SCHOOL_HOUSE_TEAMS,
  announceInterHouseChampion,
  getCustomHouses
} from '../utils/quizData';
import { 
  playCorrectChime, 
  playWrongBuzzer, 
  playCountdownBeep, 
  playWinFanfare, 
  playTickSound 
} from '../utils/soundEffects';
import { getSettings } from '../utils/db';
import { useGameExitRoute } from '../utils/navigation';
import ConfettiEffect from '../components/ConfettiEffect';

const { 
  FiClock, FiAward, FiArrowRight, FiArrowLeft, 
  FiMaximize, FiMinimize, FiUsers, FiSun, FiMoon, 
  FiEye, FiRefreshCw, FiZap 
} = FiIcons;

// Helper to scramble a word deterministically or randomly without matching original
const scrambleString = (word) => {
  const clean = word.replace(/\s+/g, '');
  const arr = clean.split('');
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  const result = arr.join('');
  return result === clean ? scrambleString(word) : result;
};

const LiveWordScramble = () => {
  const { exitRoute, exitTitle } = useGameExitRoute();
  const [words] = useState(DEFAULT_SCRAMBLE_WORDS);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [houseScores, setHouseScores] = useState(getHouseScores());
  
  // Day / Night Theme Mode
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    return document.documentElement.classList.contains('dark') || localStorage.getItem('theme') === 'dark';
  });

  const toggleDayNightMode = () => {
    const nextDark = !isDarkMode;
    setIsDarkMode(nextDark);
    if (nextDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  // Game state
  const currentItem = words[currentIndex] || words[0];
  const [scrambled, setScrambled] = useState(() => scrambleString(currentItem.word));
  const [revealedChars, setRevealedChars] = useState({});
  const [isSolved, setIsSolved] = useState(false);
  const [timeLeft, setTimeLeft] = useState(20);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [settings] = useState(getSettings());

  // Round Completion & Winner Announcement State (prevents repeat log entries)
  const [showRoundFinishedModal, setShowRoundFinishedModal] = useState(false);
  const [isRoundWinnerAnnounced, setIsRoundWinnerAnnounced] = useState(false);
  const [roundAwardType, setRoundAwardType] = useState('🏆 Speed Word Scramble Championship Shield');
  const [roundPrizeDetails, setRoundPrizeDetails] = useState('Championship Shield, Medals & Certificates awarded to winning Word Scramble house');
  const [roundAnnouncedBy, setRoundAnnouncedBy] = useState('Language Quiz Referee / Chief Coordinator');
  const [roundAnnouncedRecord, setRoundAnnouncedRecord] = useState(null);

  // Reset when word changes
  useEffect(() => {
    setScrambled(scrambleString(currentItem.word));
    setRevealedChars({});
    setIsSolved(false);
    setTimeLeft(20);
    setIsTimerActive(false);
  }, [currentIndex, currentItem.word]);

  // Timer countdown
  useEffect(() => {
    let timer = null;
    if (isTimerActive && timeLeft > 0 && !isSolved) {
      timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            setIsTimerActive(false);
            if (settings.soundEnabled !== false) playWrongBuzzer();
            return 0;
          }
          if (prev <= 5 && settings.soundEnabled !== false) {
            playCountdownBeep(prev === 2);
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [isTimerActive, timeLeft, isSolved, settings.soundEnabled]);

  const handleStartTimer = () => {
    setIsTimerActive(true);
  };

  const revealFirstLetter = () => {
    const original = currentItem.word;
    const updated = { ...revealedChars, 0: original[0] };
    setRevealedChars(updated);
    if (settings.soundEnabled !== false) playTickSound(650, 0.08);
  };

  const revealVowels = () => {
    const vowels = new Set(['A', 'E', 'I', 'O', 'U']);
    const original = currentItem.word;
    const updated = { ...revealedChars };
    for (let i = 0; i < original.length; i++) {
      if (vowels.has(original[i])) {
        updated[i] = original[i];
      }
    }
    setRevealedChars(updated);
    if (settings.soundEnabled !== false) playTickSound(720, 0.09);
  };

  const handleSolveWord = () => {
    setIsSolved(true);
    setIsTimerActive(false);
    if (settings.soundEnabled !== false) {
      playWinFanfare();
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);
    }
  };

  const nextWord = () => {
    if (currentIndex < words.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      // Completed all words! Do not repeat from beginning - show round finished screen
      setShowRoundFinishedModal(true);
      if (settings.soundEnabled !== false) playWinFanfare();
    }
  };

  const handleAnnounceRoundWinner = () => {
    if (isRoundWinnerAnnounced) return; // Prevent repeated / duplicate entries in log!

    const customHouses = getCustomHouses();
    const sortedHouses = [...customHouses].sort((a, b) => (houseScores[b.id] || 0) - (houseScores[a.id] || 0));
    const winnerHouse = sortedHouses[0] || customHouses[0];
    const winningPoints = houseScores[winnerHouse.id] || 0;

    const record = announceInterHouseChampion({
      houseId: winnerHouse.id,
      houseName: winnerHouse.name,
      badge: winnerHouse.badge,
      points: winningPoints,
      awardType: roundAwardType,
      eventSource: 'Live Speed Word Scramble',
      prizeDistributionInfo: roundPrizeDetails,
      announcedBy: roundAnnouncedBy
    });

    setIsRoundWinnerAnnounced(true);
    setRoundAnnouncedRecord(record);
    if (settings.soundEnabled !== false) playWinFanfare();
  };

  const handleRestartRound = () => {
    setCurrentIndex(0);
    setShowRoundFinishedModal(false);
    setIsRoundWinnerAnnounced(false);
    setRoundAnnouncedRecord(null);
  };

  const prevWord = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    } else {
      setCurrentIndex(words.length - 1);
    }
  };

  const updateHouseScore = (houseId, delta) => {
    const newScores = {
      ...houseScores,
      [houseId]: Math.max(0, (houseScores[houseId] || 0) + delta)
    };
    setHouseScores(newScores);
    saveHouseScores(newScores);
    if (settings.soundEnabled !== false && delta > 0) {
      playCorrectChime();
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) document.exitFullscreen().catch(() => {});
      setIsFullscreen(false);
    }
  };

  const targetLetters = currentItem.word.split('');

  return (
    <div className={`min-h-screen flex flex-col justify-between p-4 md:p-8 select-none overflow-x-hidden relative transition-colors duration-300 ${
      isDarkMode 
        ? 'bg-slate-950 text-white' 
        : 'bg-gradient-to-br from-emerald-50/70 via-sky-50/70 to-amber-50/80 text-slate-900'
    }`}>
      
      {/* Background Soft Orbs */}
      <div className={`absolute top-0 right-1/4 w-96 h-96 rounded-full blur-[100px] pointer-events-none ${
        isDarkMode ? 'bg-emerald-600/10' : 'bg-emerald-200/40'
      }`}></div>
      <div className={`absolute bottom-0 left-1/4 w-96 h-96 rounded-full blur-[100px] pointer-events-none ${
        isDarkMode ? 'bg-amber-500/10' : 'bg-amber-200/40'
      }`}></div>

      {showConfetti && <ConfettiEffect />}

      {/* TOP HEADER: Branding & Controls */}
      <header className={`relative z-20 flex flex-wrap items-center justify-between gap-4 pb-4 border-b transition-colors ${
        isDarkMode ? 'border-slate-800' : 'border-slate-200'
      }`}>
        <div className="flex items-center gap-3">
          <Link 
            to={exitRoute} 
            title={exitTitle}
            className={`w-10 h-10 rounded-xl border flex items-center justify-center transition-colors shadow-sm ${
              isDarkMode 
                ? 'bg-slate-900 border-slate-700 text-slate-300 hover:text-white' 
                : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100'
            }`}
          >
            <SafeIcon icon={FiArrowLeft} />
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping"></span>
              <h1 className="text-xl md:text-2xl font-black tracking-wider uppercase bg-clip-text text-transparent bg-gradient-to-r from-emerald-600 via-teal-600 to-indigo-600 dark:from-emerald-400 dark:via-teal-300 dark:to-cyan-400">
                Speed Word Scramble & Heritage Buzz
              </h1>
            </div>
            <span className={`text-[11px] font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Live Projector Inter-House Classroom Challenge
            </span>
          </div>
        </div>

        {/* Header Badges & Actions */}
        <div className="flex items-center gap-2">
          {/* Day / Night Switch */}
          <button
            onClick={toggleDayNightMode}
            className={`px-3 py-2 rounded-xl border text-xs font-bold flex items-center gap-1.5 transition-all shadow-sm cursor-pointer ${
              isDarkMode 
                ? 'bg-slate-900 border-slate-800 text-yellow-400 hover:bg-slate-800' 
                : 'bg-white border-amber-200 text-amber-700 hover:bg-amber-50'
            }`}
          >
            <SafeIcon icon={isDarkMode ? FiSun : FiMoon} className="text-base" />
            <span className="hidden sm:inline">{isDarkMode ? 'Day ☀️' : 'Night 🌙'}</span>
          </button>

          {/* Fullscreen Toggle */}
          <button
            onClick={toggleFullscreen}
            className={`p-2.5 rounded-xl border transition-colors shadow-sm cursor-pointer ${
              isDarkMode 
                ? 'bg-slate-900 border-slate-800 hover:bg-slate-800 text-slate-300' 
                : 'bg-white border-slate-200 hover:bg-slate-100 text-slate-700'
            }`}
          >
            <SafeIcon icon={isFullscreen ? FiMinimize : FiMaximize} />
          </button>
        </div>
      </header>

      {/* MAIN PROJECTOR DISPLAY */}
      <main className="relative z-10 flex-1 my-6 flex flex-col justify-center max-w-5xl mx-auto w-full">
        
        {/* Category & Timer Ribbon */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full border text-xs font-bold uppercase tracking-wider ${
              isDarkMode 
                ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' 
                : 'bg-emerald-100 text-emerald-800 border-emerald-200 shadow-sm'
            }`}>
              {currentItem.category}
            </span>
            <span className={`px-3 py-1 rounded-full text-xs font-mono font-bold ${
              isDarkMode ? 'bg-slate-800 text-slate-300' : 'bg-white text-slate-700 border border-slate-200 shadow-sm'
            }`}>
              Word {currentIndex + 1} of {words.length}
            </span>
          </div>

          <div className="flex items-center gap-3">
            {!isTimerActive && !isSolved && (
              <button
                onClick={handleStartTimer}
                className="px-3.5 py-1.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white text-xs font-black uppercase tracking-wider shadow-md transition-all flex items-center gap-1.5 cursor-pointer"
              >
                <SafeIcon icon={FiClock} /> Start Timer (20s)
              </button>
            )}
            <div className={`flex items-center gap-2 px-4 py-1.5 rounded-xl font-mono font-black text-lg border shadow-sm ${
              timeLeft <= 5 
                ? 'bg-rose-500/20 text-rose-500 border-rose-500/40 animate-pulse' 
                : (isDarkMode ? 'bg-slate-900 text-amber-400 border-slate-800' : 'bg-white text-amber-600 border-amber-200')
            }`}>
              <SafeIcon icon={FiClock} />
              <span>{String(timeLeft).padStart(2, '0')}s</span>
            </div>
          </div>
        </div>

        {/* SCRAMBLED LETTERS SHOWCASE CARD */}
        <div className={`p-8 md:p-12 rounded-3xl border text-center relative overflow-hidden transition-colors mb-6 ${
          isDarkMode 
            ? 'bg-slate-900/90 border-slate-800 shadow-[0_20px_50px_rgba(0,0,0,0.5)]' 
            : 'bg-white border-2 border-emerald-100 shadow-[0_15px_40px_rgba(16,185,129,0.08)]'
        }`}>
          <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-emerald-400 via-teal-300 to-indigo-500"></div>

          <span className="text-xs uppercase font-extrabold tracking-widest text-emerald-600 dark:text-emerald-400 mb-2 inline-block">
            {isSolved ? '🎉 CORRECT WORD REVEALED!' : '🧩 UNSCRAMBLE THE HERITAGE WORD:'}
          </span>

          {/* Letter Tiles Array */}
          <div className="flex flex-wrap items-center justify-center gap-2.5 sm:gap-4 my-4">
            {!isSolved ? (
              scrambled.split('').map((char, idx) => (
                <motion.div
                  key={idx}
                  animate={{ y: [0, -6, 0] }}
                  transition={{ repeat: Infinity, duration: 1.8 + (idx % 3) * 0.3 }}
                  className={`w-12 h-14 sm:w-16 sm:h-20 rounded-2xl flex items-center justify-center font-black text-2xl sm:text-4xl shadow-md border-2 ${
                    isDarkMode 
                      ? 'bg-gradient-to-br from-slate-800 to-slate-900 border-slate-700 text-white' 
                      : 'bg-gradient-to-br from-white to-slate-100 border-slate-300 text-slate-900 shadow-sm'
                  }`}
                >
                  {char}
                </motion.div>
              ))
            ) : (
              targetLetters.map((char, idx) => (
                <motion.div
                  key={idx}
                  initial={{ scale: 0.5, rotateY: 90 }}
                  animate={{ scale: 1, rotateY: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="w-12 h-14 sm:w-16 sm:h-20 rounded-2xl flex items-center justify-center font-black text-2xl sm:text-4xl shadow-lg border-2 bg-gradient-to-br from-emerald-500 to-teal-600 border-emerald-300 text-white"
                >
                  {char}
                </motion.div>
              ))
            )}
          </div>

          {/* Solution Target Line (reveals partial hints) */}
          {!isSolved && (
            <div className="flex items-center justify-center gap-2 sm:gap-3 mt-6">
              {targetLetters.map((char, idx) => {
                const isCharRevealed = revealedChars[idx];
                const isSpace = char === ' ';
                if (isSpace) {
                  return <div key={idx} className="w-4 sm:w-6"></div>;
                }
                return (
                  <div 
                    key={idx}
                    className={`w-9 h-11 sm:w-12 sm:h-14 rounded-xl border-b-4 flex items-center justify-center font-black text-lg sm:text-2xl transition-all ${
                      isCharRevealed 
                        ? 'bg-amber-400 text-slate-950 border-amber-600 shadow-sm' 
                        : (isDarkMode ? 'bg-slate-950/60 border-slate-700 text-slate-500' : 'bg-slate-100 border-slate-300 text-slate-400')
                    }`}
                  >
                    {isCharRevealed || ''}
                  </div>
                );
              })}
            </div>
          )}

          {/* Word Hint Bar */}
          <div className={`mt-6 p-4 rounded-2xl border text-sm md:text-base font-semibold max-w-2xl mx-auto ${
            isDarkMode 
              ? 'bg-slate-950/60 border-slate-800 text-slate-300' 
              : 'bg-slate-50 border-slate-200 text-slate-800'
          }`}>
            <span className="text-amber-500 font-black mr-2">HINT:</span>
            {currentItem.hint}
          </div>

          {/* Educational Fun Fact (When Solved) */}
          {isSolved && currentItem.funFact && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`mt-4 p-4 rounded-2xl border max-w-2xl mx-auto flex items-center gap-3 text-left ${
                isDarkMode ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' : 'bg-emerald-50 border-emerald-200 text-emerald-950'
              }`}
            >
              <span className="text-2xl">💡</span>
              <div className="text-xs sm:text-sm font-medium">
                <span className="font-black uppercase block text-[10px] tracking-wider">Heritage Fact:</span>
                {currentItem.funFact}
              </div>
            </motion.div>
          )}
        </div>

        {/* TEACHER / STAGE CONTROLLER ACTION BAR */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            {!isSolved && (
              <>
                <button
                  type="button"
                  onClick={revealFirstLetter}
                  className={`px-4 py-2.5 rounded-xl border text-xs font-bold transition-all shadow-xs cursor-pointer ${
                    isDarkMode ? 'bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-800' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <SafeIcon icon={FiEye} className="inline mr-1" /> Reveal 1st Letter
                </button>
                <button
                  type="button"
                  onClick={revealVowels}
                  className={`px-4 py-2.5 rounded-xl border text-xs font-bold transition-all shadow-xs cursor-pointer ${
                    isDarkMode ? 'bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-800' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  <SafeIcon icon={FiZap} className="inline mr-1 text-amber-500" /> Reveal Vowels
                </button>
                <button
                  type="button"
                  onClick={() => setScrambled(scrambleString(currentItem.word))}
                  className={`px-3 py-2.5 rounded-xl border text-xs font-bold transition-all shadow-xs cursor-pointer ${
                    isDarkMode ? 'bg-slate-900 border-slate-700 text-slate-300 hover:bg-slate-800' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-100'
                  }`}
                  title="Reshuffle Scramble"
                >
                  <SafeIcon icon={FiRefreshCw} />
                </button>
              </>
            )}
          </div>

          <div className="flex items-center gap-2">
            {!isSolved ? (
              <button
                type="button"
                onClick={handleSolveWord}
                className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white font-black text-xs uppercase tracking-wider shadow-lg transition-transform active:scale-95 cursor-pointer"
              >
                Reveal Answer 🌟
              </button>
            ) : (
              <button
                type="button"
                onClick={nextWord}
                className="px-6 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-xs uppercase tracking-wider shadow-lg transition-transform active:scale-95 cursor-pointer flex items-center gap-1.5"
              >
                Next Word <SafeIcon icon={FiArrowRight} />
              </button>
            )}
          </div>
        </div>

      </main>

      {/* FOOTER: School House Teams Barometer & Paging */}
      <footer className={`relative z-20 pt-4 space-y-4 border-t transition-colors ${
        isDarkMode ? 'border-slate-800' : 'border-slate-200'
      }`}>
        
        {/* House Points Barometer */}
        <div className={`p-3 rounded-2xl border transition-colors ${
          isDarkMode ? 'bg-slate-900/80 border-slate-800' : 'bg-white/90 border-slate-200 shadow-sm'
        }`}>
          <div className="flex items-center justify-between mb-2 px-1">
            <span className={`text-[10px] uppercase font-bold tracking-wider flex items-center gap-1.5 ${
              isDarkMode ? 'text-slate-400' : 'text-slate-600'
            }`}>
              <SafeIcon icon={FiUsers} /> Live House Points Barometer (Tap to score)
            </span>
            <span className={`text-[10px] font-mono ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
              Awards +10 to fast answering house
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {SCHOOL_HOUSE_TEAMS.map(team => {
              const score = houseScores[team.id] || 0;
              return (
                <div key={team.id} className={`p-2.5 rounded-xl border flex items-center justify-between transition-colors ${
                  isDarkMode ? 'bg-slate-950 border-slate-800' : 'bg-slate-50 border-slate-200'
                }`}>
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{team.badge}</span>
                    <div>
                      <span className={`text-xs font-bold block leading-tight ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                        {team.name}
                      </span>
                      <span className="text-[10px] font-mono text-amber-500 font-black">{score} pts</span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => updateHouseScore(team.id, 10)}
                      className="px-2 py-1 bg-emerald-500/20 hover:bg-emerald-500 text-emerald-600 dark:text-emerald-300 hover:text-white font-black text-[10px] rounded-lg border border-emerald-500/30 transition-colors cursor-pointer"
                    >
                      +10
                    </button>
                    <button
                      type="button"
                      onClick={() => updateHouseScore(team.id, -5)}
                      className="px-1.5 py-1 bg-red-500/10 hover:bg-red-500 text-red-600 dark:text-red-400 hover:text-white font-black text-[10px] rounded-lg border border-red-500/30 transition-colors cursor-pointer"
                    >
                      -5
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Word Navigation Bar */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={prevWord}
            className={`px-5 py-2.5 rounded-xl border text-xs font-bold flex items-center gap-2 transition-colors shadow-sm cursor-pointer ${
              isDarkMode 
                ? 'bg-slate-900 border-slate-700 hover:bg-slate-800 text-slate-300' 
                : 'bg-white border-slate-300 hover:bg-slate-100 text-slate-700'
            }`}
          >
            <SafeIcon icon={FiArrowLeft} /> Previous Word
          </button>

          <button
            type="button"
            onClick={nextWord}
            className={`px-5 py-2.5 rounded-xl text-xs font-black flex items-center gap-2 transition-all shadow-md cursor-pointer ${
              currentIndex === words.length - 1
                ? 'bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 text-slate-950 shadow-amber-500/30 animate-pulse'
                : 'bg-emerald-600 hover:bg-emerald-500 text-white'
            }`}
          >
            {currentIndex === words.length - 1 ? (
              <>Finish & Announce Winner 🏆</>
            ) : (
              <>Next Word <SafeIcon icon={FiArrowRight} /></>
            )}
          </button>
        </div>

      </footer>

      {/* WORD SCRAMBLE ROUND COMPLETED & WINNER ANNOUNCEMENT MODAL */}
      <AnimatePresence>
        {showRoundFinishedModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/85 backdrop-blur-md overflow-y-auto"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className={`border-2 rounded-3xl p-6 sm:p-8 max-w-xl w-full shadow-2xl relative text-left my-8 overflow-hidden ${
                isDarkMode ? 'bg-slate-900 border-amber-500/50 text-white' : 'bg-white border-amber-400 text-slate-900'
              }`}
            >
              {/* Header Gold Bar */}
              <div className="absolute top-0 inset-x-0 h-1.5 bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500"></div>

              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2.5 py-0.5 rounded-full bg-amber-500/20 text-amber-500 border border-amber-500/30 text-[10px] font-black uppercase">
                      Round Finished
                    </span>
                    <span className="text-xs text-slate-400 font-bold">&bull; All {words.length} Words Solved</span>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-black flex items-center gap-2">
                    <span>🎉 Speed Scramble Completed!</span>
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Review final house points, announce the round winner, and record prize distribution details.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setShowRoundFinishedModal(false)}
                  className="p-1.5 rounded-xl text-slate-400 hover:text-slate-200"
                >
                  <SafeIcon icon={FiX} className="text-xl" />
                </button>
              </div>

              {/* House Points Leaderboard */}
              <div className="mb-5">
                <div className="text-[11px] font-black uppercase tracking-wider text-slate-400 mb-2">
                  Final House Points Standings:
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {(() => {
                    const customHouses = getCustomHouses();
                    const sorted = [...customHouses].sort((a, b) => (houseScores[b.id] || 0) - (houseScores[a.id] || 0));
                    return sorted.map((house, idx) => (
                      <div
                        key={house.id}
                        className={`p-2.5 rounded-xl border text-center relative ${
                          idx === 0
                            ? 'bg-amber-500/15 border-amber-500/40 text-amber-400 ring-1 ring-amber-400'
                            : isDarkMode ? 'bg-slate-800/60 border-slate-700' : 'bg-slate-50 border-slate-200'
                        }`}
                      >
                        {idx === 0 && (
                          <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.2 rounded-full bg-amber-400 text-slate-950 text-[9px] font-black uppercase">
                            👑 Winner
                          </span>
                        )}
                        <div className="text-2xl mt-1">{house.badge || '🏆'}</div>
                        <div className="text-xs font-black truncate">{house.name}</div>
                        <div className="text-xs font-mono font-bold text-slate-400 mt-0.5">
                          {houseScores[house.id] || 0} pts
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>

              {/* Winner Announcement Section (Prevents repeated / duplicate logs) */}
              <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/80 mb-5 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-black uppercase text-amber-500 flex items-center gap-1.5">
                    <SafeIcon icon={FiAward} /> Winner Announcement to Sports Day Log
                  </span>
                  {isRoundWinnerAnnounced && (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-[10px] font-black uppercase">
                      ✓ Logged Once
                    </span>
                  )}
                </div>

                {!isRoundWinnerAnnounced ? (
                  <>
                    <div>
                      <label className="block text-[11px] font-bold text-slate-400 mb-1">
                        Award Trophy / Plaque Title:
                      </label>
                      <input
                        type="text"
                        value={roundAwardType}
                        onChange={e => setRoundAwardType(e.target.value)}
                        className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-xl px-3 py-2 text-xs font-bold outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] font-bold text-slate-400 mb-1">
                        Prize Distribution Information:
                      </label>
                      <textarea
                        rows={2}
                        value={roundPrizeDetails}
                        onChange={e => setRoundPrizeDetails(e.target.value)}
                        placeholder="Details for distributing prizes on stage..."
                        className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-xl px-3 py-1.5 text-xs font-medium outline-none"
                      />
                    </div>

                    <button
                      type="button"
                      onClick={handleAnnounceRoundWinner}
                      className="w-full py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 font-black text-xs shadow-md hover:brightness-110 active:scale-95 transition-all flex items-center justify-center gap-2 cursor-pointer"
                    >
                      <SafeIcon icon={FiAward} />
                      <span>Announce Winner to Sports Day Log & Champions 🏆</span>
                    </button>
                  </>
                ) : (
                  <div className="p-3 rounded-xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-xs space-y-1">
                    <div className="font-black flex items-center gap-1.5 text-emerald-400">
                      <span>✓ Official Winner Announcement Logged</span>
                    </div>
                    <div>
                      <strong>{roundAnnouncedRecord?.houseName}</strong> crowned as <strong>{roundAnnouncedRecord?.awardType}</strong> ({roundAnnouncedRecord?.points} pts).
                    </div>
                    <div className="text-[11px] text-slate-400">
                      Recorded in Sports Day Log & Match History for prize coordination. Announcement will not repeat.
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-3 border-t border-slate-200 dark:border-slate-700/60">
                <button
                  type="button"
                  onClick={() => setShowRoundFinishedModal(false)}
                  className="px-4 py-2 rounded-xl text-slate-400 hover:text-slate-200 text-xs font-bold"
                >
                  Close Summary
                </button>

                <button
                  type="button"
                  onClick={handleRestartRound}
                  className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs flex items-center gap-1.5 shadow-md cursor-pointer"
                >
                  <SafeIcon icon={FiRefreshCw} />
                  <span>Start New Word Round</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default LiveWordScramble;
